import { Fragment, useEffect, useState } from "react";
import BFLLink from "@repo/shared-ui/BFLLink";
import Products from "./Products";
import ImportantLinks from "./ImportantLinks";
import {
  handleFooterAccordionClickDL,
  handleFooterLinkClickDL,
  handleFooterSocialMediaClickDL,
} from "./FooterAccordionFunction";
import Conditional from "@repo/shared-ui/Conditional";

interface FooterLinkItem {
  linkTitle?: string;
  linkUrl?: string;
  nodeName?: string;
  children?: FooterLinkItem[];
}

interface ProductSectionItem {
  sectionTitle?: string;
  titleUrl?: string;
  links?: FooterLinkItem[];
}

interface SocialMediaItem {
  titleText?: string;
  socialMediaUrl?: string;
}

type FooterSectionKey =
  | "languages"
  | "applicationForms"
  | "productsPortfolio"
  | "calculators"
  | "importantLinks"
  | "reachUs";

interface SectionTitles {
  languages: string;
  applicationForms: string;
  productsPortfolio: string;
  calculators: string;
  importantLinks: string;
  reachUs: string;
}

interface AccordionItem {
  key: FooterSectionKey;
  displayTitle: string;
  content: FooterLinkItem[] | ProductSectionItem[] | SocialMediaItem[];
}

interface FooterAccordionProps {
  sectionTitles: SectionTitles;
  languagesData: FooterLinkItem[];
  loanLinks: FooterLinkItem[];
  reachUsData: FooterLinkItem[];
  calculatorData: FooterLinkItem[];
  importantLinksData: FooterLinkItem[];
  productsData: ProductSectionItem[];
  socialMediaIcons: SocialMediaItem[];
}

// Helper function to handle link click
const handleLinkClickEvent = (
  e: React.MouseEvent<HTMLElement>,
  linkTitle: string,
  sectionTitle: string,
  linkUrl: string
) => {
  e.preventDefault();
  handleFooterLinkClickDL(linkTitle, sectionTitle, linkUrl);
  window.location.href = linkUrl;
};

// Helper function to render application form links
const renderApplicationFormLinks = (
  content: FooterLinkItem[],
  sectionTitle: string,
  isMobile: boolean
) => {
  const contentArr = Array.isArray(content) ? content : [];
  const first = contentArr[0];

  const links = first && Array.isArray(first.children) ? first.children : contentArr;

  return links.map((link: FooterLinkItem, idx: number) => {
    const linkUrl = link.linkUrl || "#";
    const linkTitle = link?.linkTitle || "";

    return (
      <div
        key={link?.nodeName || linkTitle || idx}
        className={`my-1 box-border pr-2 leading-[19.2px] break-words ${isMobile ? "w-[50%]" : "w-[20%]"}`}
        style={{ width: isMobile ? "50%" : "20%" }}
      >
        <BFLLink
          href={linkUrl}
          className="block w-full"
          onClick={(e) => handleLinkClickEvent(e, linkTitle, sectionTitle, linkUrl)}
        >
          {linkTitle}
        </BFLLink>
      </div>
    );
  });
};

const renderRegularLinks = (
  content: FooterLinkItem[],
  sectionTitle: string,
  isReachUs: boolean,
  isMobile: boolean,
  isFiveColumn?: boolean
) => {
  return content?.map((link, idx) => {
    const linkUrl = link.linkUrl || "#";
    const linkTitle = link?.linkTitle || "";

    if (isReachUs) {
      return (
        <BFLLink
          key={link?.nodeName || linkTitle || idx}
          href={linkUrl}
          className={`my-1 w-[50%] leading-[19.2px] ${isReachUs ? "pr-0 md:w-[50%]" : "pr-2"}`}
          onClick={(e) => handleLinkClickEvent(e, linkTitle, sectionTitle, linkUrl)}
        >
          {linkTitle}
        </BFLLink>
      );
    }

    if (isFiveColumn) {
      return (
        <div
          key={link?.nodeName || linkTitle || idx}
          className={`my-1 box-border pr-2 leading-[19.2px] break-words ${isMobile ? "w-[50%]" : "w-[20%]"}`}
          style={{ width: isMobile ? "50%" : "20%" }}
        >
          <BFLLink
            href={linkUrl}
            className="block w-full"
            onClick={(e) => handleLinkClickEvent(e, linkTitle, sectionTitle, linkUrl)}
          >
            {linkTitle}
          </BFLLink>
        </div>
      );
    }

    return (
      <BFLLink
        key={link?.nodeName || linkTitle || idx}
        href={linkUrl}
        className="my-1 w-[50%] pr-2 leading-[19.2px]"
        onClick={(e) => handleLinkClickEvent(e, linkTitle, sectionTitle, linkUrl)}
      >
        {linkTitle}
      </BFLLink>
    );
  });
};

// Helper function to render links based on section type
const renderLinks = (item: AccordionItem, isMobile: boolean) => {
  if (item.key === "applicationForms") {
    return renderApplicationFormLinks(
      item.content as FooterLinkItem[],
      item.displayTitle,
      isMobile
    );
  }
  return renderRegularLinks(
    item.content as FooterLinkItem[],
    item.displayTitle,
    item.key === "reachUs",
    isMobile,
    item.key === "calculators"
  );
};

const renderAccordionContent = (
  item: AccordionItem,
  productsData: ProductSectionItem[],
  socialMediaIcons: SocialMediaItem[],
  isMobile: boolean
) => {
  if (item.key === "productsPortfolio") {
    return <Products data={productsData} />;
  }

  if (item.key === "reachUs") {
    return (
      <div className={`flex gap-4 pt-2 ${isMobile ? "flex-col" : "flex-row items-start"}`}>
        <div className={`flex flex-wrap text-sm ${isMobile ? "w-full" : "w-[400px] md:w-[500px]"}`}>
          {renderLinks(item, isMobile)}
        </div>

        <div className="flex shrink-0 gap-3 py-1">
          {socialMediaIcons.map(({ titleText, socialMediaUrl }, idx) =>
            RenderSocialMediaIcons(titleText || "", socialMediaUrl || "", idx, item.displayTitle)
          )}
        </div>
      </div>
    );
  }

  if (item.key === "importantLinks") {
    return (
      <ImportantLinks
        links={item.content as FooterLinkItem[]}
        sectionTitle={item.displayTitle}
        isMobile={isMobile}
      />
    );
  }

  return <div className="flex w-full flex-wrap pt-2 text-sm">{renderLinks(item, isMobile)}</div>;
};

const RenderSocialMediaIcons = (type: string, url: string, index: number, sectionTitle: string) => {
  const commonClass = "w-8 h-8 rounded-full p-2  text-sm";
  const ytClass = "bg-yt-red";
  const fbClass = "bg-fb-blue";
  const xClass = "bg-black";
  const lInClass = "bg-lin-blue";

  const handleSocialMediaClick = (
    e: React.MouseEvent<HTMLElement>,
    platform: string,
    mediaUrl: string,
    tabPosition: number
  ) => {
    e.preventDefault();

    // Track analytics before navigation
    handleFooterSocialMediaClickDL(platform, mediaUrl, sectionTitle, tabPosition);

    // Navigate after analytics completes
    window.location.href = mediaUrl;
  };

  return (
    <Fragment key={`${type}-${index}`}>
      <Conditional if={type === "Facebook"}>
        <BFLLink
          href={url}
          className={`${commonClass} ${fbClass}`}
          onClick={(e) => handleSocialMediaClick(e, type, url, index)}
        >
          <i className="icomoon before:content-['\e915']" />
        </BFLLink>
      </Conditional>
      <Conditional if={type === "LinkedIn"}>
        <BFLLink
          href={url}
          className={`${commonClass} ${lInClass}`}
          onClick={(e) => handleSocialMediaClick(e, type, url, index)}
        >
          <i className="icomoon before:content-['\e917']" />
        </BFLLink>
      </Conditional>
      <Conditional if={type === "Twitter"}>
        <BFLLink
          href={url}
          className={`${commonClass} ${xClass}`}
          onClick={(e) => handleSocialMediaClick(e, type, url, index)}
        >
          <i className="icomoon before:content-['\e95a']" />
        </BFLLink>
      </Conditional>
      <Conditional if={type === "YouTube"}>
        <BFLLink
          href={url}
          className={`${commonClass} ${ytClass}`}
          onClick={(e) => handleSocialMediaClick(e, type, url, index)}
        >
          <i className="icomoon before:content-['\e920']" />
        </BFLLink>
      </Conditional>
    </Fragment>
  );
};

const FooterAccordion: React.FC<FooterAccordionProps> = ({
  sectionTitles,
  languagesData,
  loanLinks,
  reachUsData,
  calculatorData,
  importantLinksData,
  productsData,
  socialMediaIcons,
}) => {
  // Changed: Moved hooks inside the main component body
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on initial load
    checkScreenSize();

    // Listen for window resizes
    window.addEventListener("resize", checkScreenSize);

    // Cleanup listener on unmount
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const accordionData: AccordionItem[] = [
    {
      key: "languages",
      displayTitle: sectionTitles.languages,
      content: languagesData,
    },
    {
      key: "applicationForms",
      displayTitle: sectionTitles.applicationForms,
      content: loanLinks,
    },
    {
      key: "productsPortfolio",
      displayTitle: sectionTitles.productsPortfolio,
      content: productsData,
    },
    {
      key: "calculators",
      displayTitle: sectionTitles.calculators,
      content: Object.values(calculatorData),
    },
    {
      key: "importantLinks",
      displayTitle: sectionTitles.importantLinks,
      content: importantLinksData,
    },
    {
      key: "reachUs",
      displayTitle: sectionTitles.reachUs,
      content: Object.values(reachUsData),
    },
  ];

  const toggleAccordion = (index: number) => {
    // Track analytics event for accordion click
    const title = accordionData[index]?.displayTitle ?? "";
    // Pass loginStatus (empty string), cta_text (accordion title), and tab_position (index)
    handleFooterAccordionClickDL(title, index);

    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="mx-auto w-full">
      {accordionData.map((item, index) => (
        <div key={"footacc-" + index} className="border-stealblue-100 border-b px-4 py-3">
          <button
            type="button"
            onClick={() => toggleAccordion(index)}
            className="bg-bfl-blue flex w-full cursor-pointer items-center justify-between text-left text-base font-medium capitalize transition"
            data-section-title={item.displayTitle}
          >
            {item.displayTitle}
            <i
              className={String.raw`icomoon transform transition-transform duration-300 before:content-['\e909'] ${
                openIndex === index ? "rotate-180" : ""
              }`}
            ></i>
          </button>
          <div
            className={`bg-bfl-blue transition-all duration-300 ease-in-out ${
              openIndex === index ? "" : "hidden"
            } ${item.key === "reachUs" ? " " : "block"}`}
            style={{ WebkitFontSmoothing: "auto", MozOsxFontSmoothing: "auto" }}
          >
            {renderAccordionContent(item, productsData, socialMediaIcons, isMobile)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default FooterAccordion;
